<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { Label, RadioGroup } from "bits-ui";
  import { useData } from "$lib/state/data.svelte.js";
  import Sparkline from "$components/Sparkline/Sparkline.svelte";
  import { formatValue } from "$utils/formats.js";

  /** @typedef {import("$utils/types.d.ts").UnitType} UnitType */

  const toolData = useData();
  const METRIC_CATS = ["costs", "distress"];
  const METRIC_CAT_LABELS = [toolData.labels.costs_label, toolData.labels.distress_label];

  /**
   * @typedef {Object} MetricItem
   * @property {string} series - The value of the metric.
   * @property {string} label - The label of the metric.
   * @property {Array<Object<any, any>>} timeSeries
   * @property {string} category
   * @property {number} recentValue
   * @property {UnitType} units
   */

  /**
   * @type {{ value: string, metrics: MetricItem[] }}
   */
  let { value = $bindable(), metrics } = $props();
  let currentIndex = $derived(getIndex(value));
  let metricEls = $state(new Array(metrics.length));
  function onMetricSelect(/** @type {any} */ e) {
    value = e.currentTarget.dataset.value;
  }

  function selectNextMetric() {
    const nextIndex = (currentIndex + 1) % metrics.length;
    value = metrics[nextIndex].series;
    metricEls[nextIndex]?.focus();
  }

  function selectPreviousMetric() {
    const prevIndex = (currentIndex - 1 + metrics.length) % metrics.length;
    value = metrics[prevIndex].series;
    metricEls[prevIndex]?.focus();
  }

  function scrollToTool() {
    if (typeof window !== "undefined" && document && toolData.toolContainerEl) {
      const toolEl = toolData.toolContainerEl.querySelector("#data-tool-primary-tool-container");
      toolEl?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function getIndex(/** @type {string} */ metric) {
    return metrics.findIndex((/** @type {MetricItem} */ m) => m.series === metric);
  }

  function onMetricClick(/** @type {any} */ e) {
    onMetricSelect(e);
    scrollToTool();
  }
</script>

<RadioGroup.Root bind:value>
  <div class="metric-category-columns">
    {#each METRIC_CATS as cat, i}
      {@const catMetrics = metrics.filter((/** @type {MetricItem} */ m) => m.category === cat)}
      <div class="metric-category-container">
        <div class="metric-group-label">{METRIC_CAT_LABELS[i]}</div>
        <div class="metric-items-container">
          {#each catMetrics as metric, i (metric.series)}
            {@const isActive = metric.series === value}
            {@const metricIndex = getIndex(metric.series)}
            <RadioGroup.Item value={metric.series} id={metric.series}>
              {#snippet child()}
                <div
                  class="metric-item"
                  class:active={isActive}
                  data-value={metric.series}
                  data-checked={isActive}
                  onclick={onMetricClick}
                  bind:this={metricEls[metricIndex]}
                  onkeydown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      onMetricSelect(e);
                    }
                    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                      selectNextMetric();
                      e.preventDefault();
                    }
                    if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                      selectPreviousMetric();
                      e.preventDefault();
                    }
                  }}
                  tabindex={isActive ? 0 : -1}
                >
                  {#if metric.timeSeries.length > 1}
                    <div class="metric-sparkline-wrap">
                      <Sparkline data={metric.timeSeries} height={50} />
                    </div>
                  {/if}
                  <div class="metric-value">{formatValue(metric.recentValue, metric)}</div>
                  <div class="metric-label">{metric.label}</div>
                </div>
              {/snippet}
            </RadioGroup.Item>
          {/each}
        </div>
      </div>
    {/each}
  </div></RadioGroup.Root
>

<style>
  .metric-item {
    background-color: var(--color-gray-shade-lightest);
    padding: var(--spacing-4);
    max-width: 400px;
    cursor: pointer;
    border-left: solid 8px var(--color-gray-shade-lightest);
  }

  .metric-item.active {
    border-left: solid 8px var(--color-blue-shade-dark);
  }
  .metric-item:focus-visible {
    outline: 2px solid var(--color-blue-shade-darkest);
    outline-offset: 2px;
  }
  .metric-value {
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-2xl);
  }
  .metric-sparkline-wrap {
    margin-block-end: var(--spacing-4);
  }
  .metric-group-label {
    font-style: italic;
    margin-block-end: var(--spacing-4);
  }
  .metric-items-container {
    display: grid;
    align-items: start;
    grid-template-columns: repeat(2, minmax(150px, 1fr));
    gap: var(--spacing-4);
  }
  @media (min-width: 1024px) {
    .metric-category-columns {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--spacing-6);
    }
  }
  @media (max-width: 768px) {
    .metric-value {
      font-size: var(--font-size-xl);
    }
  }
</style>
